// Pure mapping from a Better Auth organization-plugin error (as returned by
// authClient.organization.getInvitation / acceptInvitation) to plain-English copy for
// AcceptInvitation.jsx. No DOM/browser APIs here so it stays unit-testable in isolation,
// same pattern as authErrors.js (which covers a different vocabulary — the /login/error
// query-string codes — not these organization-plugin error codes).
//
// Better Auth's organization plugin (node_modules/better-auth/dist/plugins/organization/
// error-codes.mjs) wraps each named code as { code, message, toString() }, and the error
// surfaces on the client as `result.error.code` set to one of those names. One case has no
// code at all: the not-found/expired/already-accepted path (organization.mjs's getInvitation/
// acceptInvitation both call APIError.fromStatus('BAD_REQUEST', { message: 'Invitation not
// found!' }) with no `code` field) — the server does not distinguish those three, so neither
// do we.
//
// Shape returned for every case: { title, body }.

const KNOWN_CODES = {
  YOU_ARE_NOT_THE_RECIPIENT_OF_THE_INVITATION: {
    title: "This isn't your invitation",
    body: "This invitation was sent to a different email address than the one you're signed in with.",
  },
  EMAIL_VERIFICATION_REQUIRED_BEFORE_ACCEPTING_OR_REJECTING_INVITATION: {
    title: 'Verify your email',
    body: "Verify your email address before accepting this invitation. Check your inbox for the verification link, or ask us to resend it.",
  },
  EMAIL_VERIFICATION_REQUIRED_FOR_INVITATION: {
    title: 'Verify your email',
    body: "Verify your email address to view this invitation. Check your inbox for the verification link, or ask us to resend it.",
  },
  ORGANIZATION_MEMBERSHIP_LIMIT_REACHED: {
    title: "This organisation is full",
    body: "This organisation has reached its member limit. Contact whoever invited you.",
  },
  authentication_method_required: {
    title: 'Use the required sign-in method',
    body: 'This invitation requires sign-in through its organisation identity provider before it can be accepted.',
  },
  verification_required: {
    title: 'Verify your email',
    body: 'Verify your email address before accepting this invitation. You can request a new verification email below.',
  },
  invitation_not_available: {
    title: 'Invitation not available',
    body: 'This invitation is no longer available. It may have expired, been used, or been withdrawn.',
  },
  invitation_provider_mismatch: {
    title: 'Use the required sign-in method',
    body: 'This invitation is bound to a different organisation sign-in method. Start again from this invitation link.',
  },
  invitation_identity_mismatch: {
    title: 'Use the invited account',
    body: 'Sign in with the account that received this invitation before accepting it.',
  },
};

/** Better Auth SDK responses put the server's error text in `error`, while named
 * organization-plugin errors use `message` and `code`. Keep both shapes at this UI boundary. */
function messageFor(error) {
  if (typeof error?.message === 'string') return error.message;
  if (typeof error?.error === 'string') return error.error;
  return '';
}

/**
 * @param {{ code?: string, status?: number, message?: string, error?: string } | null | undefined} error
 * @returns {{ title: string, body: string }}
 */
export function getInvitationErrorInfo(error) {
  const code = error?.code;
  if (code && KNOWN_CODES[code]) return KNOWN_CODES[code];
  const message = messageFor(error);
  if (/verif/i.test(message)) return KNOWN_CODES.verification_required;
  if (/recipient|different email|account that received/i.test(message)) return KNOWN_CODES.YOU_ARE_NOT_THE_RECIPIENT_OF_THE_INVITATION;
  if (/authentication method|required sign[- ]?in/i.test(message)) return KNOWN_CODES.authentication_method_required;
  return {
    title: 'Invitation not available',
    body: 'This invitation link is no longer valid. It may have expired, already been used, or been withdrawn.',
  };
}

/** True for the two "verify your email" codes above — used to decide whether to offer a resend link. */
export function isEmailVerificationError(error) {
  return error?.code === 'EMAIL_VERIFICATION_REQUIRED_BEFORE_ACCEPTING_OR_REJECTING_INVITATION' || error?.code === 'EMAIL_VERIFICATION_REQUIRED_FOR_INVITATION' || /verif/i.test(messageFor(error));
}

/** True for the "signed in as the wrong person" code — used to offer a sign-out link. */
export function isNotRecipientError(error) {
  return error?.code === 'YOU_ARE_NOT_THE_RECIPIENT_OF_THE_INVITATION' || /recipient|different email|account that received/i.test(messageFor(error));
}

export default getInvitationErrorInfo;
